/**
 * Validation Guards for Insurance Connector
 *
 * Guards that use Zod schemas to validate form data before state transitions
 */

import {
  birthdaySchema,
  insurancePayerSchema,
  insuredStateSchema,
  memberIdSchema,
  nameSchema,
  phoneNumberSchema,
  primaryInsuranceSchema,
  secondaryInsuranceSchema,
  stateSchema,
} from "../schemas/validation.schemas";
import type {
  InsuranceConnectorContext,
  InsuranceConnectorEvent,
} from "../types";

/**
 * Helper to extract data from either event or context
 */
function getFormData(
  context: InsuranceConnectorContext,
  event: InsuranceConnectorEvent,
): InsuranceConnectorContext | Record<string, unknown> {
  if (event.type === "SUBMIT") {
    return event.data;
  }
  return context;
}

/**
 * Validate name input (first name and last name)
 */
export const hasValidName = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  const data = getFormData(context, event);
  const result = nameSchema.safeParse(data);
  return result.success;
};

/**
 * Validate birthday input
 */
export const hasValidBirthday = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  const data = getFormData(context, event);
  const result = birthdaySchema.safeParse(data);
  return result.success;
};

/**
 * Validate phone number input
 */
export const hasValidPhoneNumber = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  const data = getFormData(context, event);
  const result = phoneNumberSchema.safeParse(data);
  return result.success;
};

/**
 * Validate consumer state selection
 */
export const hasValidState = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  const data = getFormData(context, event);
  const result = stateSchema.safeParse(data);
  return result.success;
};

/**
 * Validate insurance payer selection
 */
export const hasValidInsurancePayer = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  const data = getFormData(context, event);
  const result = insurancePayerSchema.safeParse(data);
  return result.success;
};

/**
 * Validate member ID input
 */
export const hasValidMemberId = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  const data = getFormData(context, event);
  const result = memberIdSchema.safeParse(data);
  return result.success;
};

/**
 * Validate insured state selection
 */
export const hasValidInsuredState = ({
  context,
  event,
}: {
  context: InsuranceConnectorContext;
  event: InsuranceConnectorEvent;
}) => {
  const data = getFormData(context, event);
  const result = insuredStateSchema.safeParse(data);
  return result.success;
};

/**
 * Validate complete primary insurance data
 */
export const hasValidPrimaryInsurance = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  const result = primaryInsuranceSchema.safeParse(context.primaryInsurance);
  return result.success;
};

/**
 * Validate complete secondary insurance data
 */
export const hasValidSecondaryInsurance = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  if (!context.secondaryInsurance) return false;
  const result = secondaryInsuranceSchema.safeParse(context.secondaryInsurance);
  return result.success;
};

/**
 * Check if user wants to add secondary insurance
 */
export const wantsSecondaryInsurance = ({
  event,
}: {
  event: InsuranceConnectorEvent;
}) => {
  return event.type === "YES_SECONDARY";
};

/**
 * Check if all required data is present for submission
 */
export const hasAllRequiredData = ({
  context,
}: {
  context: InsuranceConnectorContext;
}) => {
  return !!(
    context.firstName &&
    context.lastName &&
    context.birthday &&
    context.primaryInsurance?.payer &&
    context.primaryInsurance?.memberId &&
    context.primaryInsurance?.insuredState
  );
};
